import type { EditTarget, LayoutBlock } from '@web-studio/shared-schemas';
import { defaultContactFields } from '../../lib/defaultFormFields';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { parseFormFields, type ParsedFormField } from '../../lib/parseFormFields';
import { safeMailtoHref, safeMapEmbedUrl, safeTelHref } from '../../lib/safeGuestLinks';
import { parseHoursSchedule, type HoursScheduleRow } from '../../lib/splitHoursLines';
import { contactHoursLine, contactLandmark } from '../../lib/contactSummary';
import { resolveSectionLucideIcon } from '../../icons/ctaLucideMap';
import type { LucideIcon as LucideIconType } from 'lucide-react';

export type ContactView = {
  sectionTitle: string;
  sectionIcon: LucideIconType;
  intro: string;
  address: string;
  directions: string;
  landmark: string;
  phone: string;
  email: string;
  hours: string;
  hoursLine: string;
  hoursRows: HoursScheduleRow[];
  formHeading: string;
  formSupporting: string;
  consent: string;
  ctaLabel: string;
  fields: ParsedFormField[];
  consentFromFields?: ParsedFormField;
  formKey: string;
  mapEmbedUrl?: string;
  mapImageSrc?: string;
  mapImageSrcSet?: string;
  showMap: boolean;
  telHref?: string;
  mailHref?: string;
  target: EditTarget | null;
  editable?: boolean;
};

export function readContactView(input: {
  block: LayoutBlock;
  locale: string;
  isActive?: boolean;
  activeEditTarget?: EditTarget | null;
  editable?: boolean;
}): ContactView {
  const { block, locale, editable } = input;
  const hours = pickBlockField(block.content, 'hours', locale);
  const directions = pickBlockField(block.content, 'directions', locale);
  const phone = pickBlockField(block.content, 'phone', locale);
  const email = pickBlockField(block.content, 'email', locale);
  const hoursRows = parseHoursSchedule(hours);
  const operational = parseFormFields(pickBlockField(block.content, 'formFields', locale));
  const mapImage = pickMediaUrl(block, 'mapImage');
  const mapEmbedUrl = safeMapEmbedUrl(pickBlockField(block.content, 'mapEmbedUrl', locale));
  const mapImageSrc = mapImage ? buildSiteImageProxyUrl(mapImage, 1200, 78) : undefined;

  return {
    sectionTitle: pickBlockField(block.content, 'sectionTitle', locale),
    sectionIcon: resolveSectionLucideIcon(pickBlockField(block.content, 'sectionIcon', locale), 'location'),
    intro: pickBlockField(block.content, 'intro', locale),
    address: pickBlockField(block.content, 'address', locale),
    directions,
    landmark: contactLandmark(directions),
    phone,
    email,
    hours,
    hoursLine: contactHoursLine(hoursRows, hours),
    hoursRows,
    formHeading: pickBlockField(block.content, 'formHeading', locale),
    formSupporting: pickBlockField(block.content, 'formSupporting', locale),
    consent: pickBlockField(block.content, 'consent', locale),
    ctaLabel: pickBlockField(block.content, 'ctaLabel', locale),
    fields:
      operational.length > 0
        ? operational.filter((field) => field.name !== 'consent')
        : defaultContactFields(locale),
    consentFromFields: operational.find((field) => field.name === 'consent'),
    formKey: block.dataBinding?.kind === 'form' ? block.dataBinding.formKey : 'contact',
    mapEmbedUrl,
    mapImageSrc,
    mapImageSrcSet: mapImage ? buildSiteImageSrcSet(mapImage, [480, 768, 1200], 78) : undefined,
    showMap: Boolean(mapEmbedUrl || mapImageSrc || editable),
    telHref: safeTelHref(phone),
    mailHref: safeMailtoHref(email),
    target: input.isActive ? input.activeEditTarget ?? null : null,
    editable,
  };
}
